import { BonusCampaign } from '../../api/fetchers/bonusPromotions/types/bonus';
import type { BalanceType } from '../../api/fetchers/userInfo/types';
import { ChipTypes } from '../../store/slices/chips/types';
import type { TableInfo, BetInfo } from '../../store/slices/launcher/types';

// extract enum from object
export type EnumExtractor<T> = T[keyof T];

// Ensure PayloadType extends Record<string, any>
export type EnforcePayloadType<T> = T extends Record<string, any> ? T : never;

export interface MtbBalanceType {
  amount: number;
  bonus: number;
  totalBet: number;
}

export const LAUNCHER_GROUP = {
  LAUNCHER: 'LAUNCHER',
  LOBBY: 'lobby',
  MTB: 'MTB',
} as const;

export type LauncherGroup = EnumExtractor<typeof LAUNCHER_GROUP>;
export interface LauncherTarget {
  name: string;
  type?: string;
  tableId?: string;
  window?: Window;
}

export interface LauncherPayload {
  payload?: ValidPayloadType;
  name: string;
  group: LauncherGroup;
  originTableId: string;
}

export const LAUNCHER_MESSAGES = {
  TABLE_COUNT: 'TABLE_COUNT',
  TABLES_UPDATED: 'TABLES_UPDATED',
  LOBBY_OPENED: 'LOBBY_OPENED',
  BALANCE_UPDATE: 'BALANCE_UPDATE',
  COMPUTED_BALANCE_UPDATE: 'COMPUTED_BALANCE_UPDATE',
  BET_PLACED: 'BET_PLACED',
  BETS_ACCEPTED: 'BETS_ACCEPTED',
  BETS_UPDATED: 'BETS_UPDATED',
  REDIRECT_GAME: 'REDIRECT_GAME',
  TOGGLE_MTB: 'TOGGLE_MTB',
  LOBBY_LOADED: 'lobbyLoaded',
  LOBBY_CLOSE: 'close',
  GAME_ADD: 'addGame',
  MUTE_ALL_BUT_THIS: 'muteAllButThis',
  BETS_CLOSED_STATUS: 'betsClosedStatus',
  UPDATE_ALL_BETS_CLOSED_STATUS: 'updateAllBetsClosedStatus',
  UPDATE_ALL_BETS: 'updateAllBets',
  REPLACE_TABLE: 'replaceTable',
  // game dispatch
  GAME_LAUNCHED: 'GAME_LAUNCHED',
  OPEN_LAUNCHER_LOBBY: 'OPEN_LAUNCHER_LOBBY',
  OPEN_LAUNCHER_MTB: 'OPEN_LAUNCHER_MTB',
  GAME_CLOSE: 'closeGame',
  BETS_CLOSED: 'betsClosed',
  INITIALIZE_BETS_INFO: 'INITIALIZE_BETS_INFO',
  SET_ACTIVE_TABLE: 'SET_ACTIVE_TABLE',
  THEME_CHANGE: 'THEME_CHANGE',
  TOGGLE_NO_COMMISSION: 'TOGGLE_NO_COMMISSION',
  CHANGE_LANGUAGE: 'CHANGE_LANGUAGE',
  WIN_RESULT: 'winResult',
  // Freebets
  BONUS_CAMPAIGNS: 'BONUS_CAMPAIGNS',
  SHOW_POPUP: 'SHOW_POPUP',
} as const;

export type LauncherMessages = EnumExtractor<typeof LAUNCHER_MESSAGES>;

export interface GameLaunchedPayload {
  operatorGameId: string;
}
export interface GamesLaunchedPayload {
  tableIds: string[];
}
export interface GameClosePayload {
  tableId: string;
  operatorGameId?: string;
}

export interface BaseLauncherPayload {
  tableId: string;
}

export interface BalanceUpdatePayload {
  balance: BalanceType;
}

export interface MtbBalanceUpdatePayload {
  balance: MtbBalanceType;
}

export interface BetsPlacedPayload {
  tableId: string;
  betAmount: number;
  betType: ChipTypes;
  bonusId: number;
}

export interface TablesUpdatePayload {
  tables: TableInfo[];
}

export interface AddMTBPayload {
  url: string;
}

export interface PopupPayload {
  popupType: string;
}

export interface AddGamePayload {
  id: string;
  launcherId: string;
  tableId: string;
  url: string;
}

export interface ThemeChangePayload {
  theme: string;
}

export interface RedirectPayload {
  url: string;
}

export interface WinResultPayload {
  tableId: string;
  winAmount: number;
}
export interface ToggleMtbPayload {
  isMinimize: boolean;
}
export interface BetInfoPayload {
  betInfo: BetInfo;
}

export interface BonusCampaignPayload {
  activeBonusCampaign: BonusCampaign;
}

export interface ReplaceTablePayload {
  url: string;
  tableId: string;
  operatorGameId: string;
}

export interface ToggleNoCommissionPayload {
  isNoCommission: boolean;
}

export interface ChangeLanguagePayload {
  lang: string;
}

type PayloadType =
  | GameLaunchedPayload
  | GamesLaunchedPayload
  | AddGamePayload
  | GameClosePayload
  | TablesUpdatePayload
  | AddMTBPayload
  | BalanceUpdatePayload
  | MtbBalanceUpdatePayload
  | BetInfoPayload
  | BetInfo
  | BaseLauncherPayload
  | ThemeChangePayload
  | RedirectPayload
  | ToggleMtbPayload
  | BetsPlacedPayload
  | BonusCampaignPayload
  | ReplaceTablePayload
  | ToggleNoCommissionPayload
  | ChangeLanguagePayload
  | PopupPayload;

type ValidPayloadType = EnforcePayloadType<PayloadType>;
